import React, { useEffect, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';

// On-screen camera control panel (?cam=edit).
//
// Split in two because of renderer boundaries: react-dom DOM can't render
// inside the r3f tree. CameraBridge lives INSIDE the Canvas and hands the
// live controls/camera out through a module-level ref; CameraPanel is plain
// DOM rendered OUTSIDE the Canvas, mutating the controls directly.

export const camBridge = { controls: null, camera: null };

export function CameraBridge() {
  const { controls, camera } = useThree();
  useFrame(() => {
    camBridge.controls = controls;
    camBridge.camera = camera;
  });
  return null;
}

const row = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, marginTop: 6 };
const lab = { width: 96, color: '#ffd27d', flexShrink: 0 };
const val = { width: 44, textAlign: 'right', color: '#ffe9c4', fontVariantNumeric: 'tabular-nums' };

function Slider({ label, min, max, step, value, onChange }) {
  return (
    <div style={row}>
      <span style={lab}>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))} style={{ flex: 1 }} />
      <span style={val}>{Number(value).toFixed(2)}</span>
    </div>
  );
}

export default function CameraPanel() {
  const roomBounds = useWorldStore((s) => s.roomBounds);
  const boundsEnabled = useWorldStore((s) => s.boundsEnabled);
  const setBoundsEnabled = useWorldStore((s) => s.setBoundsEnabled);
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);
  const bumpCamVersion = useWorldStore((s) => s.bumpCamVersion);

  const [cfg, setCfg] = useState(null);
  const [live, setLive] = useState(null);
  // Tilt (pitch) limits for firstPerson rooms — how far up/down you can look.
  const [lookCfg, setLookCfg] = useState({ tiltMin: -1.45, tiltMax: 1.45 });

  // Load the room's tilt limits (saved override → rooms.js default).
  useEffect(() => {
    let lim = ROOMS[interior]?.look || {};
    try { const s = JSON.parse(localStorage.getItem(`look:${interior}`) || 'null'); if (s) lim = s; } catch (_) {}
    setLookCfg({
      tiltMin: Number.isFinite(lim.tiltMin) ? lim.tiltMin : -1.45,
      tiltMax: Number.isFinite(lim.tiltMax) ? lim.tiltMax : 1.45,
    });
  }, [interior]);

  const applyLook = (next) => {
    setLookCfg(next);
    if (interior) localStorage.setItem(`look:${interior}`, JSON.stringify(next));
    bumpCamVersion(); // InteriorWalk re-reads the limits
  };

  // Re-snapshot from the live controls whenever the zoom level changes —
  // each level applies its own constraints, and writing a stale snapshot
  // back (street's zoom-min inside a 3m room) deadlocks the camera.
  useEffect(() => { setCfg(null); }, [zoom]);

  // Wait for the controls to exist (OrbitControls mounts after first paint),
  // then keep sampling readouts.
  useEffect(() => {
    const id = setInterval(() => {
      const c = camBridge.controls;
      const cam = camBridge.camera;
      if (!cam) return;
      // c is absent in firstPerson rooms (no OrbitControls) — the panel still
      // shows the live readout + "copy spawn" from the view direction.
      setCfg((prev) => prev || (c ? {
        hasControls: true,
        rotateSpeed: c.rotateSpeed,
        zoomSpeed: c.zoomSpeed,
        dampingFactor: c.dampingFactor,
        minDistance: c.minDistance,
        maxDistance: c.maxDistance,
        minPolarAngle: c.minPolarAngle,
        maxPolarAngle: c.maxPolarAngle,
        limitAzimuth: Number.isFinite(c.minAzimuthAngle),
        minAzimuthAngle: Number.isFinite(c.minAzimuthAngle) ? c.minAzimuthAngle : -Math.PI / 2,
        maxAzimuthAngle: Number.isFinite(c.maxAzimuthAngle) ? c.maxAzimuthAngle : Math.PI / 2,
        enablePan: c.enablePan
      } : { hasControls: false }));
      const t = c?.target;
      const fwd = new THREE.Vector3(); cam.getWorldDirection(fwd);
      setLive({
        pos: cam.position.toArray(),
        target: t ? [t.x, t.y, t.z] : null,
        forward: [fwd.x, fwd.y, fwd.z],
        dist: t ? cam.position.distanceTo(t) : 0,
        polar: c?.getPolarAngle?.() ?? 0,
        azimuth: c?.getAzimuthalAngle?.() ?? 0
      });
    }, 180);
    return () => clearInterval(id);
  }, []);

  // Sliders write straight onto the live controls object AND persist per
  // room (localStorage) so tuning survives reloads and room transitions —
  // same contract as panel placement. rooms.js is the shipped default;
  // the saved override wins on this machine until reset.
  const persist = (next) => {
    if (!interior) return;
    const saved = {
      min: next.minDistance, max: next.maxDistance,
      minPolar: next.minPolarAngle, maxPolar: next.maxPolarAngle,
      ...(next.limitAzimuth ? { minAz: next.minAzimuthAngle, maxAz: next.maxAzimuthAngle } : {}),
      pan: next.enablePan,
      rotateSpeed: next.rotateSpeed, zoomSpeed: next.zoomSpeed, damping: next.dampingFactor
    };
    localStorage.setItem(`orbit:${interior}`, JSON.stringify(saved));
    bumpCamVersion();
  };
  const apply = (next) => {
    setCfg(next);
    persist(next);
    const c = camBridge.controls;
    if (!c) return;
    c.rotateSpeed = next.rotateSpeed;
    c.zoomSpeed = next.zoomSpeed;
    c.dampingFactor = next.dampingFactor;
    c.minDistance = next.minDistance;
    c.maxDistance = next.maxDistance;
    c.minPolarAngle = next.minPolarAngle;
    c.maxPolarAngle = next.maxPolarAngle;
    c.minAzimuthAngle = next.limitAzimuth ? next.minAzimuthAngle : -Infinity;
    c.maxAzimuthAngle = next.limitAzimuth ? next.maxAzimuthAngle : Infinity;
    c.enablePan = next.enablePan;
  };

  const panelBox = {
    position: 'fixed', right: 12, top: 60, zIndex: 40, width: 290,
    background: 'rgba(16,10,4,0.92)', border: '1px solid rgba(255,178,94,0.4)',
    borderRadius: 12, padding: '12px 14px', color: '#ffe9c4',
    fontFamily: 'ui-monospace, monospace', pointerEvents: 'auto'
  };
  // Always show the panel shell when ?cam=edit — so it never silently vanishes
  // while the camera bridge is still warming up (or in firstPerson rooms).
  if (!cfg || !live) {
    return (
      <div style={panelBox}>
        <div style={{ fontSize: 12, letterSpacing: 1, color: '#ffb25e', marginBottom: 4 }}>CAMERA · ?cam=edit</div>
        <div style={{ fontSize: 11, opacity: 0.7 }}>connecting to camera…</div>
      </div>
    );
  }
  const r = (v) => Math.round(v * 100) / 100;
  const orbitLine = cfg.hasControls
    ? `orbit: { min: ${r(cfg.minDistance)}, max: ${r(cfg.maxDistance)}, minPolar: ${r(cfg.minPolarAngle)}, maxPolar: ${r(cfg.maxPolarAngle)}${cfg.limitAzimuth ? `, minAz: ${r(cfg.minAzimuthAngle)}, maxAz: ${r(cfg.maxAzimuthAngle)}` : ''}${cfg.enablePan ? ', pan: true' : ''} }`
    : '';
  // Spawn look-at: the orbit target when present, else a point 2m down the
  // camera's forward (firstPerson has no orbit pivot).
  const spawnLook = live.target || [live.pos[0] + live.forward[0] * 2, live.pos[1] + live.forward[1] * 2, live.pos[2] + live.forward[2] * 2];

  return (
    <div style={panelBox}>
      <div style={{ fontSize: 12, letterSpacing: 1, color: '#ffb25e', marginBottom: 4 }}>
        CAMERA · ?cam=edit{cfg.hasControls ? '' : ' · first-person (drag+WASD)'}
      </div>

      {/* firstPerson rooms: tilt (pitch) limits — how far up/down you can look.
          tilt max = look-up ceiling, tilt min = look-down floor (radians). */}
      {!cfg.hasControls && (
        <>
          <Slider label="tilt up ↑" min={0} max={1.5} step={0.02} value={lookCfg.tiltMax} onChange={(v) => applyLook({ ...lookCfg, tiltMax: v })} />
          <Slider label="tilt down ↓" min={-1.5} max={0} step={0.02} value={lookCfg.tiltMin} onChange={(v) => applyLook({ ...lookCfg, tiltMin: v })} />
          <div style={row}>
            <span style={lab}>bounds</span>
            <input type="checkbox" checked={boundsEnabled} onChange={(e) => setBoundsEnabled(e.target.checked)} />
            <span style={{ fontSize: 10.5, opacity: 0.7 }}>keep camera in room</span>
          </div>
        </>
      )}

      {cfg.hasControls && (
        <>
          <Slider label="rotate speed" min={0.1} max={2} step={0.05} value={cfg.rotateSpeed} onChange={(v) => apply({ ...cfg, rotateSpeed: v })} />
          <Slider label="zoom speed" min={0.1} max={3} step={0.05} value={cfg.zoomSpeed} onChange={(v) => apply({ ...cfg, zoomSpeed: v })} />
          <Slider label="damping" min={0} max={0.3} step={0.01} value={cfg.dampingFactor} onChange={(v) => apply({ ...cfg, dampingFactor: v })} />
          <Slider label="zoom min" min={0.1} max={6} step={0.1} value={cfg.minDistance} onChange={(v) => apply({ ...cfg, minDistance: Math.min(v, cfg.maxDistance) })} />
          <Slider label="zoom max" min={0.5} max={20} step={0.1} value={cfg.maxDistance} onChange={(v) => apply({ ...cfg, maxDistance: Math.max(v, cfg.minDistance) })} />
          <Slider label="polar min ↑" min={0} max={Math.PI} step={0.02} value={cfg.minPolarAngle} onChange={(v) => apply({ ...cfg, minPolarAngle: Math.min(v, cfg.maxPolarAngle) })} />
          <Slider label="polar max ↓" min={0} max={Math.PI} step={0.02} value={cfg.maxPolarAngle} onChange={(v) => apply({ ...cfg, maxPolarAngle: Math.max(v, cfg.minPolarAngle) })} />

          <div style={row}>
            <span style={lab}>limit azimuth</span>
            <input type="checkbox" checked={cfg.limitAzimuth} onChange={(e) => apply({ ...cfg, limitAzimuth: e.target.checked })} />
            <span style={{ fontSize: 10.5, opacity: 0.7 }}>lock horizontal swing</span>
          </div>
          {cfg.limitAzimuth && (
            <>
              <Slider label="azimuth min" min={-Math.PI} max={Math.PI} step={0.05} value={cfg.minAzimuthAngle} onChange={(v) => apply({ ...cfg, minAzimuthAngle: Math.min(v, cfg.maxAzimuthAngle) })} />
              <Slider label="azimuth max" min={-Math.PI} max={Math.PI} step={0.05} value={cfg.maxAzimuthAngle} onChange={(v) => apply({ ...cfg, maxAzimuthAngle: Math.max(v, cfg.minAzimuthAngle) })} />
            </>
          )}
          <div style={row}>
            <span style={lab}>pan</span>
            <input type="checkbox" checked={cfg.enablePan} onChange={(e) => apply({ ...cfg, enablePan: e.target.checked })} />
            <span style={lab}>bounds</span>
            <input type="checkbox" checked={boundsEnabled} onChange={(e) => setBoundsEnabled(e.target.checked)} />
          </div>
        </>
      )}

      <div style={{ marginTop: 8, fontSize: 10.5, lineHeight: 1.5, opacity: 0.85 }}>
        cam [{live.pos.map(r).join(', ')}]<br />
        {live.target
          ? <>target [{live.target.map(r).join(', ')}] · dist {r(live.dist)}<br />polar {r(live.polar)} · azimuth {r(live.azimuth)}<br /></>
          : <>look [{spawnLook.map(r).join(', ')}]<br /></>}
        {roomBounds
          ? <>bounds x[{r(roomBounds.min[0])}…{r(roomBounds.max[0])}] y[{r(roomBounds.min[1])}…{r(roomBounds.max[1])}] z[{r(roomBounds.min[2])}…{r(roomBounds.max[2])}]</>
          : 'bounds: none (no splat room loaded)'}
      </div>

      {cfg.hasControls && (
        <button
          onClick={() => { navigator.clipboard?.writeText(orbitLine); console.log(orbitLine); }}
          style={{
            marginTop: 8, width: '100%', background: 'rgba(255,178,94,0.15)', color: '#ffd27d',
            border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '6px 0', cursor: 'pointer', fontSize: 11.5
          }}
        >
          copy orbit config for rooms.js
        </button>
      )}
      {!cfg.hasControls && (
        <button
          onClick={() => {
            const line = `look: { tiltMin: ${r(lookCfg.tiltMin)}, tiltMax: ${r(lookCfg.tiltMax)} },`;
            navigator.clipboard?.writeText(line); console.log(line);
          }}
          style={{
            marginTop: 8, width: '100%', background: 'rgba(255,178,94,0.15)', color: '#ffd27d',
            border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '6px 0', cursor: 'pointer', fontSize: 11.5
          }}
        >
          copy look (tilt) config for rooms.js
        </button>
      )}
      {/* Spawn = the entry framing. Frame the room (orbit/pan or drag+WASD),
          then copy: camera position + look-at become the room's spawn. */}
      <button
        onClick={() => {
          const line = `spawn: { pos: [${live.pos.map(r).join(', ')}], look: [${spawnLook.map(r).join(', ')}] },`;
          navigator.clipboard?.writeText(line); console.log(line);
        }}
        style={{
          marginTop: 6, width: '100%', background: 'rgba(157,255,125,0.14)', color: '#c7ffb0',
          border: '1px solid rgba(157,255,125,0.4)', borderRadius: 8, padding: '6px 0', cursor: 'pointer', fontSize: 11.5
        }}
      >
        copy spawn (this framing) for rooms.js
      </button>
      {interior && (
        <button
          onClick={() => {
            localStorage.removeItem(`orbit:${interior}`);
            localStorage.removeItem(`look:${interior}`);
            const rl = ROOMS[interior]?.look || {};
            setLookCfg({ tiltMin: Number.isFinite(rl.tiltMin) ? rl.tiltMin : -1.45, tiltMax: Number.isFinite(rl.tiltMax) ? rl.tiltMax : 1.45 });
            setCfg(null); bumpCamVersion();
          }}
          style={{
            marginTop: 6, width: '100%', background: 'transparent', color: 'rgba(255,233,196,0.6)',
            border: '1px solid rgba(255,178,94,0.2)', borderRadius: 8, padding: '5px 0', cursor: 'pointer', fontSize: 11
          }}
        >
          reset this room to rooms.js defaults
        </button>
      )}
    </div>
  );
}
